import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { contactFormSchema, type ContactFormData } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Clock, Loader2, Mail } from "lucide-react";

const TOPIC_OPTIONS = [
  { value: "I have a question about pricing or plans", slug: "pricing" },
  { value: "I want to learn more about the platform", slug: "platform" },
  { value: "I'm interested in a partnership or integration", slug: "partnership" },
  { value: "I have a general inquiry", slug: "general" },
  { value: "Other", slug: "other" },
];

export default function Support() {
  useCsrfToken();
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      const response = await apiRequest("POST", "/api/contact", data);
      return response.json();
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast({
        title: "Message Sent",
        description: "We've received your message and will get back to you soon.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send message",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ContactFormData) => {
    contactMutation.mutate(data);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen">
        <MarketingHeader />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <Card className="text-center p-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2" data-testid="text-success-title">
              Message Sent Successfully
            </h2>
            <p className="text-muted-foreground mb-6">
              Thank you for reaching out. We typically respond within 24–48 hours.
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                form.reset();
              }}
              data-testid="button-send-another"
            >
              Send Another Message
            </Button>
          </Card>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <MarketingHeader />
      <div className="max-w-2xl mx-auto px-6 py-16">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-5">
            <Mail className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-3" data-testid="text-contact-title">
            Get In Touch
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
            Have a question or want to learn more? We'd love to hear from you.
            We respond to every message within 24–48 hours.
          </p>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="flex items-center gap-3 py-4">
              <div className="flex-shrink-0 w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                <Mail className="w-4 h-4 text-primary-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5" data-testid="text-email-label">
                  Email us directly
                </p>
                <a
                  href="mailto:hello@fillthetrucks.com"
                  className="text-sm font-medium text-primary"
                  data-testid="text-email-value"
                >
                  hello@fillthetrucks.com
                </a>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-3 py-4">
              <div className="flex-shrink-0 w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                <Clock className="w-4 h-4 text-primary-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5" data-testid="text-response-label">
                  Response time
                </p>
                <p className="text-sm font-medium" data-testid="text-response-value">
                  Within 24–48 hours
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Form card */}
        <Card>
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

                {/* Name + Email row */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Your name <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder=""
                            {...field}
                            data-testid="input-contact-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Email address <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder=""
                            {...field}
                            data-testid="input-contact-email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Topic dropdown */}
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        What can we help you with? <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-contact-topic">
                            <SelectValue placeholder="Select a topic..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TOPIC_OPTIONS.map((option) => (
                            <SelectItem key={option.slug} value={option.value} data-testid={`option-topic-${option.slug}`}>
                              {option.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Message */}
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Message <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell us what's on your mind..."
                          className="min-h-[140px]"
                          {...field}
                          data-testid="input-contact-message"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Submit */}
                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="w-full bg-primary text-primary-foreground"
                    disabled={contactMutation.isPending}
                    data-testid="button-submit-contact"
                  >
                    {contactMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground" data-testid="text-response-note">
                    We respond to every message within 24–48 hours
                  </p>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
      <MarketingFooter />
    </div>
  );
}
